import { forwardRef, type HTMLAttributes, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface PictureProps extends HTMLAttributes<HTMLElement> {
  webp?: string;
  img?: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  // Shown when img/webp fail to load (e.g. holiday art that wasn't drawn)
  fallbackImg?: string;
  fallbackWebp?: string;
}

const Picture = forwardRef<HTMLElement, PictureProps>(
  ({ webp, img, alt, loading, fallbackImg, fallbackWebp, ...props }, ref) => {
    const [failed, setFailed] = useState(false);

    useEffect(() => setFailed(false), [img, webp]);

    const useFallback = failed && !!fallbackImg;

    return (
      <picture {...props} ref={ref}>
        <source
          className="w-full h-full"
          srcSet={useFallback ? fallbackWebp : webp}
          type="image/webp"
        />
        <img
          loading={loading}
          className="w-full h-full"
          src={useFallback ? fallbackImg : img}
          alt={alt}
          onError={fallbackImg ? (): void => setFailed(true) : undefined}
        />
      </picture>
    );
  },
);

const MotionPicture = motion.create(Picture);

export { MotionPicture, Picture };
